import React, { useState } from 'react';
import { Play, RotateCcw, TrendingUp, Activity, BarChart2, ChevronDown, Calendar, DollarSign, Zap } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useTranslation } from 'react-i18next'; 
import api from '../api/api';

const POPULAR_PAIRS = [
  'BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'BNB/USDT', 'XRP/USDT', 
  'DOGE/USDT', 'ADA/USDT', 'AVAX/USDT', 'LINK/USDT', 'DOT/USDT',
  'MATIC/USDT', 'LTC/USDT', 'ATOM/USDT', 'NEAR/USDT', 'PEPE/USDT'
];

export const BacktestPage = () => {
  const { t } = useTranslation(); 
  
  const [symbol, setSymbol] = useState('BTC/USDT');
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);

  const runBacktest = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await api.get(`/backtest/run?symbol=${symbol}&days=${days}&timeframe=5m`);
      setResult(res.data);
    } catch (e) {
      alert(t('backtest.error') || 'Error running backtest'); 
    } finally {
      setLoading(false);
    }
  };

  // Helper для цветов стратегий
  const getSignalBadge = (signal: string) => {
      const s = (signal || '').toUpperCase();
      if (s.includes('SNIPER')) return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
      if (s.includes('PANIC')) return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
      if (s.includes('FVG')) return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      if (s.includes('SCALP')) return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      return 'bg-slate-700 text-slate-300';
  };

  return (
    <div className="max-w-7xl mx-auto p-6 animate-in fade-in duration-500">
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
         <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-blue-500 to-blue-700 p-3 rounded-xl shadow-lg shadow-blue-500/20">
                <BarChart2 size={32} className="text-white" />
            </div>
            <div>
                <h1 className="text-3xl font-bold text-white">{t('backtest.title')}</h1>
                <p className="text-slate-400">{t('backtest.subtitle')}</p>
            </div>
         </div>
      </div>

      {/* CONTROLS */}
      <div className="bg-[#1e293b] border border-slate-800 rounded-3xl p-6 md:p-8 mb-8 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/5 rounded-full blur-3xl pointer-events-none"></div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end relative z-10">
            
            {/* 1. ВЫБОР МОНЕТЫ */}
            <div className="relative group">
               <label className="text-slate-400 text-xs font-bold uppercase mb-2 block tracking-wider flex items-center gap-1">
                  <DollarSign size={12}/> {t('backtest.pairLabel')}
               </label>
               <div className="relative">
                  <select 
                    value={symbol} 
                    onChange={e => setSymbol(e.target.value)}
                    className="w-full bg-[#0f172a] text-white p-4 pr-10 rounded-xl border border-slate-700 font-mono text-lg appearance-none focus:border-blue-500 outline-none cursor-pointer hover:border-slate-600 transition-colors shadow-inner"
                  >
                    {POPULAR_PAIRS.map(pair => (
                        <option key={pair} value={pair}>{pair}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" size={20} />
               </div>
            </div>

            {/* 2. ПЕРИОД */}
            <div>
               <label className="text-slate-400 text-xs font-bold uppercase mb-2 block tracking-wider flex items-center gap-1">
                  <Calendar size={12}/> {t('backtest.periodLabel')}
               </label>
               <input 
                 type="number" 
                 min="1" max="30"
                 value={days} 
                 onChange={e => setDays(parseInt(e.target.value))}
                 className="w-full bg-[#0f172a] text-white p-4 rounded-xl border border-slate-700 font-mono text-lg focus:border-blue-500 outline-none transition-colors shadow-inner"
               />
            </div>

            {/* 3. КНОПКА ЗАПУСКА */}
            <button 
              onClick={runBacktest} 
              disabled={loading}
              className={`
                h-full w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 shadow-lg transition-all
                ${loading 
                    ? 'bg-slate-700 text-slate-400 cursor-wait' 
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white hover:scale-[1.02] shadow-blue-600/25 active:scale-95'
                }
              `}
            >
              {loading ? (
                  <><RotateCcw className="animate-spin" /> {t('backtest.processing')}</> 
              ) : (
                  <><Play fill="currentColor" /> {t('backtest.runBtn')}</>
              )} 
            </button>
        </div>
      </div>

      {/* --- CONTENT AREA --- */}
      
      {/* 1. НАЧАЛЬНЫЙ ЭКРАН */}
      {!result && !loading && (
         <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="bg-slate-800/50 border border-slate-700 p-6 rounded-2xl hover:bg-slate-800 transition-colors">
               <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Activity className="text-blue-400" size={24} />
               </div>
               <h3 className="text-white font-bold text-lg mb-2">{t('backtest.info1Title')}</h3>
               <p className="text-slate-400 text-sm leading-relaxed">{t('backtest.info1Desc')}</p> 
            </div>

            <div className="bg-slate-800/50 border border-slate-700 p-6 rounded-2xl hover:bg-slate-800 transition-colors">
               <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Zap className="text-purple-400" size={24} />
               </div>
               <h3 className="text-white font-bold text-lg mb-2">{t('backtest.info2Title')}</h3>
               <p className="text-slate-400 text-sm leading-relaxed">{t('backtest.info2Desc')}</p>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 p-6 rounded-2xl hover:bg-slate-800 transition-colors">
               <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4">
                  <TrendingUp className="text-green-400" size={24} />
               </div>
               <h3 className="text-white font-bold text-lg mb-2">{t('backtest.info3Title')}</h3>
               <p className="text-slate-400 text-sm leading-relaxed">{t('backtest.info3Desc')}</p>
            </div>
         </div>
      )}

      {/* 2. РЕЗУЛЬТАТЫ */}
      {result && (
        <div className="space-y-8 animate-in slide-in-from-bottom-8 duration-700">
           
           {/* KPI CARDS */}
           <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatBox 
                label={t('backtest.totalProfit')} 
                value={`${result.totalProfitPercent > 0 ? '+' : ''}${result.totalProfitPercent}%`} 
                color={result.totalProfitPercent >= 0 ? 'text-green-400' : 'text-red-400'}
                bg={result.totalProfitPercent >= 0 ? 'bg-green-500/10 border-green-500/20' : 'bg-red-500/10 border-red-500/20'}
              />
              <StatBox label={t('backtest.winRate')} value={`${result.winRate}%`} />
              <StatBox label={t('backtest.totalTrades')} value={result.totalTrades} />
              <StatBox label={t('backtest.drawdown')} value={`-${result.maxDrawdown}%`} color="text-red-400" />
           </div>

           {/* ГРАФИК */}
           <div className="bg-[#1e293b] border border-slate-800 rounded-3xl p-6 h-[450px] shadow-xl relative">
              <h3 className="text-white font-bold mb-6 flex items-center gap-2 text-lg">
                  <TrendingUp className="text-blue-500"/> {t('backtest.equityCurve')}
              </h3>
              <ResponsiveContainer width="100%" height="90%">
                 <AreaChart data={result.equityCurve}>
                    <defs>
                       <linearGradient id="colorPnL" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                       </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                    <XAxis dataKey="time" hide />
                    <YAxis stroke="#64748b" fontSize={12} domain={['auto', 'auto']} tickFormatter={(val) => `$${val}`}/>
                    <Tooltip 
                        contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '12px', color: '#fff' }}
                        labelFormatter={(label) => new Date(label).toLocaleString()}
                    />
                    <Area type="monotone" dataKey="balance" stroke="#3b82f6" strokeWidth={3} fill="url(#colorPnL)" animationDuration={1500}/>
                 </AreaChart>
              </ResponsiveContainer>
           </div>

           {/* СТАТИСТИКА ПО СТРАТЕГИЯМ */}
           {result.strategyStats && (
               <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                   {Object.entries(result.strategyStats).map(([name, stats]: any) => (
                       <div key={name} className="bg-slate-900/50 border border-slate-800 p-4 rounded-2xl flex justify-between items-center hover:border-blue-500/50 transition-colors">
                           <div>
                               <div className="text-xs text-slate-500 font-bold uppercase mb-1">{name.replace('_', ' ')}</div>
                               <div className="text-white font-bold">{stats.trades} Trades</div>
                           </div>
                           <div className={`text-right ${stats.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                               <div className="text-lg font-black">{stats.profit >= 0 ? '+' : ''}${stats.profit.toFixed(0)}</div>
                               <div className="text-xs opacity-70">{stats.winRate}% Win</div>
                           </div>
                       </div>
                   ))}
               </div>
           )}

           {/* ТАБЛИЦА СДЕЛОК */}
           <div className="bg-[#1e293b] border border-slate-800 rounded-3xl overflow-hidden shadow-xl">
              <div className="p-6 border-b border-slate-800 flex justify-between items-center">
                  <h3 className="text-white font-bold flex items-center gap-2">
                     <Activity className="text-purple-500"/> {t('backtest.history')}
                  </h3>
                  <div className="text-slate-500 text-xs">Last 50 trades</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-slate-300">
                    <thead className="bg-slate-900 font-bold uppercase text-xs text-slate-500">
                        <tr>
                        <th className="p-5">{t('backtest.colSignal')}</th>
                        <th className="p-5 text-center">{t('backtest.colType')}</th>
                        <th className="p-5 text-right">{t('backtest.colEntry')}</th>
                        <th className="p-5 text-right">{t('backtest.colExit')}</th>
                        <th className="p-5 text-right">{t('backtest.colResult')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                        {result.trades.slice(0, 50).map((t: any, i: number) => (
                        <tr key={i} className="hover:bg-slate-800/50 transition-colors">
                            <td className="p-5">
                                <span className={`px-2 py-1 rounded-md text-[10px] font-bold border ${getSignalBadge(t.signalType)}`}>
                                    {t.signalType}
                                </span>
                                <div className="text-[9px] text-slate-500 mt-1 pl-1">
                                    {new Date(t.entryTime).toLocaleString()}
                                </div>
                            </td>
                            <td className="p-5 text-center">
                                <span className={`px-2 py-1 rounded text-[10px] font-black uppercase ${t.type === 'BUY' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                    {t.type}
                                </span>
                            </td>
                            <td className="p-5 text-right font-mono text-slate-400">${t.entryPrice}</td>
                            <td className="p-5 text-right font-mono text-slate-400">${t.exitPrice}</td>
                            <td className="p-5 text-right">
                                <div className={`font-bold ${t.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {t.profit >= 0 ? '+' : ''}${t.profit.toFixed(2)}
                                </div>
                                <div className="text-[10px] text-slate-500">ROI {t.netPercent}%</div>
                            </td>
                        </tr>
                        ))}
                    </tbody>
                </table>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

const StatBox = ({ label, value, color = 'text-white', bg = 'bg-slate-800 border-slate-700' }: any) => (
    <div className={`p-6 rounded-2xl border ${bg} transition-transform hover:scale-[1.02]`}>
        <div className="text-xs uppercase font-bold text-slate-400 mb-2">{label}</div>
        <div className={`text-3xl md:text-4xl font-black ${color}`}>{value}</div>
    </div>
);